import React from 'react';
import { Tooltip } from '@myua/kit';
import { TM } from '../tokens';
import type { LocalOpKind } from '../localOps/types';
import { isInstalled } from '../localOps/install';
import { CutoutIcon, UpscaleIcon } from '../icons';

// On-device tools, pinned to the bottom of the right sidebar as a single row of
// two compact icon buttons. These are not templates (generation modes behind
// "Generate") — they're instant local image operations on the current canvas
// image, free and without an API key. The full description (runs locally,
// downloads a model on first use) lives in the hover tooltip, not on screen.

const TOOLS: { kind: LocalOpKind; caption: string; icon: React.ReactNode; describe: string }[] = [
  {
    kind: 'removeBg',
    caption: 'Remove BG',
    icon: <CutoutIcon size={18} />,
    describe: 'Cut out the subject as a transparent PNG.',
  },
  {
    kind: 'upscale',
    caption: 'Upscale 2×',
    icon: <UpscaleIcon size={18} />,
    describe: 'Double the image resolution with AI.',
  },
];

// Tooltip copy: what the tool does + that it runs locally and downloads the
// model on first use. When there's nothing to process, explain that instead.
const tooltipText = (describe: string, kind: LocalOpKind, canRun: boolean): string => {
  if (!canRun) return 'Add a reference or generate an image first.';
  const model = isInstalled(kind)
    ? 'The model is already downloaded and cached on your device.'
    : 'Downloads a model on first use (one-time), then runs from cache.';
  return `${describe} Runs locally in your browser — free, no API key. ${model}`;
};

export const LocalToolsSection: React.FC<{
  onRun: (kind: LocalOpKind) => void;
  // There is a processable image (a result, or at least a primary reference).
  canRun: boolean;
  // A local op or a generation is in progress — block new runs.
  busy: boolean;
  // The op currently running, if any (highlights its button).
  runningKind: LocalOpKind | null;
}> = ({ onRun, canRun, busy, runningKind }) => (
  <div
    style={{
      // Pin to the bottom of the sidebar, set off by a divider.
      marginTop: 'auto',
      paddingTop: 12,
      borderTop: `1px solid ${TM.border}`,
      display: 'flex',
      gap: 8,
    }}
  >
    {TOOLS.map((t) => {
      const running = runningKind === t.kind;
      const disabled = busy || !canRun;
      return (
        <Tooltip key={t.kind} title={tooltipText(t.describe, t.kind, canRun)} placement="top">
          {/* Span wrapper so the tooltip still shows when the button is disabled. */}
          <span style={{ flex: 1, display: 'flex' }}>
            <button
              type="button"
              onClick={() => onRun(t.kind)}
              disabled={disabled}
              style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 5,
                padding: '10px 6px',
                borderRadius: 6,
                border: `1px solid ${running ? TM.blue : TM.border}`,
                background: running ? TM.blueWash : '#fff',
                color: running ? TM.blue : TM.text,
                cursor: disabled ? 'not-allowed' : 'pointer',
                // Let the wrapping span receive hover (for the tooltip) while disabled.
                pointerEvents: disabled ? 'none' : 'auto',
                opacity: disabled && !running ? 0.55 : 1,
              }}
            >
              <span style={{ display: 'inline-flex', color: running ? TM.blue : TM.textMute }}>
                {t.icon}
              </span>
              <span style={{ fontSize: 11, fontWeight: 600 }}>
                {running ? 'Working…' : t.caption}
              </span>
            </button>
          </span>
        </Tooltip>
      );
    })}
  </div>
);
